/**
 * „Alltag starten“ / „Alltag beenden“ — die Seite der App. (01.10.2026, Ticket 2849)
 *
 * „Durchgehend an/aus“ für die Zuhause-Karte (zuhause-view.ts) und den Auftrag
 * per Zuruf (tools/alltag-steuern.ts): beide gehen durch `schalteDurchgehend`.
 * ⚠️ Der Schalter in den Einstellungen (settings-view.ts, `buildAlwaysRecordCard`)
 *    geht noch einen EIGENEN Weg (eigene Speicherung, eigene Meldungen, ohne
 *    Start/Stopp des Rekorders) — er ist nicht umgestellt, weil er sich anders
 *    verhält (nur die Absicht; die native Seite startet selbst).
 *
 * Der Auftrag kommt als SSE-Ereignis `auftrag` (chat-stream.ts). Die App tut
 * es und ANTWORTET mit dem Zustand, den sie danach SELBST misst — nur diese
 * Antwort lässt das Werkzeug „läuft“ sagen (Anzeige behauptet zuviel).
 *
 * ⚠️ Nur die iPhone-App (natives Plugin). Im Browser antwortet sie
 *    „nur-iphone-app“, und die Seite meldet sich gar nicht erst als Gerät an.
 * ⚠️ STARTEN nur bei sichtbarer App — die Aufnahme nimmt die Umgebung auf und
 *    startet nie heimlich. Beenden geht immer.
 * ⚠️ Läuft ein ANRUF in der App, startet der Rekorder nie (Audio-Engine-Fehler
 *    4010): es werden nur die Absicht und der Pausengrund „anruf“ gesetzt; die
 *    Aufnahme beginnt nach dem Auflegen (Wache).
 * ⚠️ Ein Auftrag gilt nur bis `gueltigBis`; ein abgelaufener tut nichts und
 *    wird nicht beantwortet. Jeder Auftrag hat eine Höchstdauer in der Kette.
 * ⚠️ Aufträge laufen nacheinander (eine Kette): ein „stop“ unmittelbar nach
 *    einem „start“ überholt es nicht.
 */
import {
  AUFTRAG_LAUF_MAX_MS,
  KANN_ALLTAG,
  istAlltagAktion,
  type AlltagZustand,
  type AuftragGrund,
  type AuftragsAntwort,
  type GeraeteAuftrag,
} from "../../shared/alltag-steuern.js";

/** Der Zustand des Rekorders (native-recorder.ts: idle, recording, paused, finishing, error). */
type RekorderZustand = string;

/** Das, was von native-recorder.ts gebraucht wird — für Tests ersetzbar. */
interface RekorderAnschluss {
  isAlwaysRecordSupported(): boolean;
  getRecorderState(): RekorderZustand;
  nimmAusZurueck(): void;
  setAlwaysDayRecording(an: boolean): Promise<boolean>;
  startRecording(cb: { onError?: (e: Error) => void }): Promise<void>;
  stopRecordingForUser(): Promise<boolean>;
  syncFromNative(): Promise<void>;
  offenePausenGruende(): string[];
  /** Einen Pausengrund eintragen, auch wenn nichts aufnimmt (Anruf): die Wache wartet dann. */
  haltePausenGrund(grund: string): void;
}

export interface Anschluss {
  rec: RekorderAnschluss;
  einstellungen: {
    dauerAn(): boolean;
    setzeDauer(an: boolean): void;
  };
  sichtbar(): boolean;
  /** Läuft gerade ein Anruf in der App? */
  anrufAktiv(): boolean;
  /** Die Absicht wurde geändert, ohne dass der Rekorder sich bewegte — Anzeigen neu zeichnen lassen. */
  absichtGeaendert?(): void;
  /** Pause zwischen zwei Messungen und längstes Warten auf den Zustand (Tests setzen kleine Werte). */
  schrittMs?: number;
  hoechstensMs?: number;
}

/** Die echte App: natives Plugin, Einstellungen, Sichtbarkeit. */
export async function echterAnschluss(): Promise<Anschluss> {
  const rec = await import("./native-recorder.js");
  const { loadSettings, saveSettings } = await import("./settings.js");
  const { istAnrufAktiv } = await import("./ptt.js");
  return {
    rec,
    einstellungen: {
      dauerAn: () => loadSettings().alwaysRecord === true,
      setzeDauer: (an) => saveSettings({ ...loadSettings(), alwaysRecord: an }),
    },
    sichtbar: () => typeof document !== "undefined" && document.visibilityState === "visible",
    anrufAktiv: istAnrufAktiv,
    absichtGeaendert: () => {
      if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("voice-v8:alltag-absicht"));
    },
  };
}

export function zustandVon(z: RekorderZustand): AlltagZustand {
  if (z === "recording") return "laeuft";
  if (z === "paused") return "pausiert";
  return "aus";
}

/** Eine Fehlermeldung des Rekorders als feste Kennung — nie als freier Text zum Server. */
export function grundAusFehler(meldung: string | null): AuftragGrund {
  return meldung && /permission|berechtigung|erlaubnis|verweigert|denied|not\s?allowed/i.test(meldung)
    ? "kein-mikro-recht"
    : "start-gescheitert";
}

const warte = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/**
 * „Durchgehend“ an oder aus — die Schritte des Schalters auf der Zuhause-Karte.
 * Einschalten nimmt ein früheres bewusstes Aus zurück, setzt die Absicht und
 * startet, wenn nichts läuft; Ausschalten beendet UND löscht die Absicht.
 */
export async function schalteDurchgehend(
  an: boolean,
  anschluss: Anschluss,
  onError?: (e: Error) => void,
): Promise<void> {
  const { rec, einstellungen } = anschluss;
  // Einschalten nimmt ein früheres bewusstes Aus zurück.
  if (an) rec.nimmAusZurueck();
  einstellungen.setzeDauer(an);
  await rec.setAlwaysDayRecording(an);
  if (an && rec.getRecorderState() === "idle") {
    await rec.startRecording(onError ? { onError } : {});
  }
  if (!an) await rec.stopRecordingForUser();
  await rec.syncFromNative().catch(() => undefined);
}

/** Wartet, bis der gemessene Zustand zum Wunsch passt (die Ereignisse des Rekorders kommen nach). */
async function messeBis(anschluss: Anschluss, passt: (z: AlltagZustand) => boolean): Promise<AlltagZustand> {
  const schritt = anschluss.schrittMs ?? 250;
  const bis = Date.now() + (anschluss.hoechstensMs ?? 3_000);
  for (;;) {
    const z = zustandVon(anschluss.rec.getRecorderState());
    if (passt(z) || Date.now() >= bis) return z;
    await warte(schritt);
    await anschluss.rec.syncFromNative().catch(() => undefined);
  }
}

/** Führt einen Auftrag aus und misst danach. Wirft nie. */
export async function fuehreAuftragAus(a: GeraeteAuftrag, anschluss: Anschluss): Promise<AuftragsAntwort> {
  const aktuell = (): AlltagZustand => zustandVon(anschluss.rec.getRecorderState());
  const nein = (grund: AuftragGrund): AuftragsAntwort => ({ ausgefuehrt: false, zustand: aktuell(), grund });
  try {
    if (a.art !== "alltag" || !istAlltagAktion(a.aktion)) return nein("unbekannte-aktion");
    if (!anschluss.rec.isAlwaysRecordSupported()) return nein("nur-iphone-app");
    if (a.aktion === "start") {
      // Nie heimlich: gestartet wird nur, wo die App vor Augen ist.
      if (!anschluss.sichtbar()) return nein("nicht-sichtbar");
      const roh = anschluss.rec.getRecorderState();
      // Ohne etwas zu verändern: sonst zöge die Wache die Aufnahme später heimlich nach.
      if (roh === "finishing" || roh === "error") return nein("gerade-nicht-moeglich");
      // Pausiert ohne Grund = er hat sie selbst pausiert. Nie von uns fortgesetzt.
      if (roh === "paused" && anschluss.rec.offenePausenGruende().length === 0) return nein("manuell-pausiert");
      if (anschluss.anrufAktiv()) {
        // Im Anruf nur die Absicht und der Grund — der Rekorder startet nie neben dem Anruf (4010).
        anschluss.rec.nimmAusZurueck();
        anschluss.einstellungen.setzeDauer(true);
        anschluss.rec.haltePausenGrund("anruf");
        anschluss.absichtGeaendert?.();
        return { ausgefuehrt: true, zustand: "pausiert", pausenGruende: anschluss.rec.offenePausenGruende().slice(0, 3) };
      }
      let fehler: string | null = null;
      await schalteDurchgehend(true, anschluss, (e) => {
        fehler = e.message || "unbekannt";
      });
      const z = await messeBis(anschluss, (x) => x !== "aus");
      if (z === "aus") {
        // Gescheitert: die Absicht zurücknehmen, sonst finge die Wache später still an, obwohl „läuft nicht“ gesagt wurde.
        anschluss.einstellungen.setzeDauer(false);
        await anschluss.rec.setAlwaysDayRecording(false).catch(() => false);
        anschluss.absichtGeaendert?.();
        return { ausgefuehrt: true, zustand: "aus", grund: grundAusFehler(fehler) };
      }
      const gruende = z === "pausiert" ? anschluss.rec.offenePausenGruende().slice(0, 3) : [];
      return { ausgefuehrt: true, zustand: z, ...(gruende.length > 0 ? { pausenGruende: gruende } : {}) };
    }
    await schalteDurchgehend(false, anschluss);
    const z = await messeBis(anschluss, (x) => x === "aus");
    return z === "aus"
      ? { ausgefuehrt: true, zustand: "aus" }
      : { ausgefuehrt: true, zustand: z, grund: "stopp-gescheitert" };
  } catch (err) {
    console.warn("[alltag-steuern] Auftrag fehlgeschlagen:", err);
    return { ausgefuehrt: true, zustand: aktuell(), grund: a.aktion === "start" ? "start-gescheitert" : "stopp-gescheitert" };
  }
}

/** Die Antwort zum Server — einmal; scheitert sie, läuft die Frist dort ab und das Werkzeug sagt „keine Antwort“. */
export async function sendeAntwort(id: string, antwort: AuftragsAntwort): Promise<boolean> {
  try {
    const r = await fetch("/api/geraet/auftrag-antwort", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ id, antwort }),
    });
    return r.ok;
  } catch {
    return false;
  }
}

let kette: Promise<unknown> = Promise.resolve();
const gesehen = new Set<string>();

/** Ist der Auftrag abgelaufen? Dann tut die App nichts und antwortet nicht. */
export function istAbgelaufen(a: GeraeteAuftrag, jetzt: number = Date.now()): boolean {
  return jetzt > a.gueltigBis;
}

/** Was die App mit einem Auftrag vom Server tut: ausführen (nacheinander), messen, antworten. */
export function behandleAuftrag(
  a: GeraeteAuftrag,
  holeAnschluss: () => Promise<Anschluss> = echterAnschluss,
  antworte: (id: string, antwort: AuftragsAntwort) => Promise<boolean> = sendeAntwort,
  hoechstensMs: number = AUFTRAG_LAUF_MAX_MS,
): Promise<void> {
  // Ein Auftrag gilt einmal (ein zweites Eintreffen derselben Kennung tut nichts) und nur bis gueltigBis.
  if (gesehen.has(a.id)) return Promise.resolve();
  gesehen.add(a.id);
  if (gesehen.size > 50) gesehen.delete(gesehen.values().next().value as string);
  if (istAbgelaufen(a)) {
    console.warn("[alltag-steuern] abgelaufenen Auftrag verworfen");
    return Promise.resolve();
  }
  const arbeit = async (): Promise<void> => {
    // Auch nach dem Warten in der Kette noch einmal: ein Auftrag, der hier ablief, tut nichts.
    if (istAbgelaufen(a)) {
      console.warn("[alltag-steuern] Auftrag in der Kette abgelaufen — verworfen");
      return;
    }
    const anschluss = await holeAnschluss();
    const antwort = await fuehreAuftragAus(a, anschluss);
    await antworte(a.id, antwort);
  };
  // Höchstdauer je Auftrag: ein hängender Start hält spätere Stopps nicht auf.
  const lauf = kette.then(() =>
    Promise.race([
      arbeit(),
      new Promise<void>((r) => {
        const t = setTimeout(r, hoechstensMs);
        (t as { unref?: () => void }).unref?.();
      }),
    ]),
  );
  kette = lauf.catch(() => undefined);
  return lauf.catch(() => undefined);
}

/**
 * Meldet sich diese Seite als eine, die den Alltag steuern kann? Nur mit dem
 * nativen Plugin; ohne Plugin (Browser) nie. Wird bei jedem (Wieder-)Verbinden
 * des Kanals neu gefragt — das Plugin steht nach einem Kaltstart erst spät.
 */
export function kannFaehigkeiten(istNativ: () => boolean): string[] {
  try {
    return istNativ() ? [KANN_ALLTAG] : [];
  } catch {
    return [];
  }
}
